// Bot admin adapter (module-local, doesn't touch adapters.ts). Backend: MyTelegram.AdminServer's
// BotsController, reading BotAggregate/BotReadModel + BotVerificationReadModel via IQueryProcessor.
// Trimmed to only what the real backend has: config (id/username/name/official/revoked/description
// /about/commands - all real BotReadModel fields), owner (OwnerUserId), and token status (prefix
// only, never the secret). Removed: webhook, mini-apps, business connections, affiliate program,
// games, message stats, app-settings/preview-media, capability restrictions - none of these exist
// in the MyTelegram backend.
import { api } from '@/lib/api'

export interface BotCommand {
  command: string
  description: string
}

export interface AdminBotConfig {
  id: number
  username: string | null
  first_name: string | null
  is_official: boolean
  revoked?: boolean
  description?: string | null
  about?: string | null
  commands: BotCommand[]
  is_banned: boolean
  ban_reason: string | null
  is_frozen: boolean
  freeze_reason: string | null
}

export async function fetchBotConfig(id: number, signal?: AbortSignal): Promise<AdminBotConfig> {
  const r = await api.get<{ bot: AdminBotConfig }>(
    `/admin/bots/${id}`,
    signal ? { signal } : undefined,
  )
  return r.bot
}

const opt = (signal?: AbortSignal) => (signal ? { signal } : undefined)

export interface BotOwner {
  id: number
  username: string | null
  first_name: string | null
  created_at: number | null
  last_seen: number | null
}

export async function fetchBotOwner(id: number, signal?: AbortSignal): Promise<BotOwner | null> {
  return (await api.get<{ owner: BotOwner | null }>(`/admin/bots/${id}/owner`, opt(signal))).owner
}

/** Token security metadata - prefix only, secret is never returned. */
export interface BotTokenInfo {
  token_prefix: string
  revoked: boolean
  revoked_at: number | null
  issued_at: number | null
  rotated_at: number | null
}

export async function fetchBotToken(id: number, signal?: AbortSignal): Promise<BotTokenInfo | null> {
  return (await api.get<{ token: BotTokenInfo | null }>(`/admin/bots/${id}/token`, opt(signal))).token
}
